import { HubUpsertParams, Hubs, OrgHubs, HubSectionType, HubResourceSection, DiscoverItemType } from 'utils/gloo-types';
import { getKit, getTemplate } from 'actions/requests';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { hubResourceForKit, hubResourceForTemplate } from 'utils/discover';
import { useThunkDispatch } from 'utils/reduxHook';
import TabNav from 'components/molecules/TabNav';
import HubDisplayPreview from './HubDisplayPreview';

export enum HubPreviewView {
  Desktop = 'Desktop View',
  Mobile = 'Mobile View',
}

interface HubPreviewProps {
  hubUpsertParams: HubUpsertParams;
}

const HubPreview = ({ hubUpsertParams }: HubPreviewProps) => {
  const [view, setView] = useState(HubPreviewView.Desktop);
  const [orgHub, setOrgHub] = useState<{ favorited: boolean }>({ favorited: false });
  const [hub, setHub] = useState<Hubs | null>(null);
  const dispatch = useThunkDispatch();

  const onViewChange = (selectedView: HubPreviewView) => {
    setView(selectedView);
  };

  const onFavorite = () => {
    setOrgHub({ favorited: true });
  };

  const onUnfavorite = () => {
    setOrgHub({ favorited: false });
  };

  // HubUpsertParams only store hub resource IDs, but HubDisplay expects the hub to have the resources loaded
  // Here, we loop over the sections of the upsert params and load all resources
  const updateHubResources = async () => {
    setHub({
      ...hubUpsertParams,
      sections: await Promise.all(
        hubUpsertParams.sections.map(async (section) =>
          section.type === HubSectionType.RESOURCE
            ? ({
                ...section,
                resources: await Promise.all(
                  section.resourceIds.map(async (resourceId) => {
                    switch (resourceId.type) {
                      case DiscoverItemType.KIT:
                        return dispatch(getKit(resourceId.id)).then(hubResourceForKit);
                      case DiscoverItemType.TEMPLATE:
                        return dispatch(getTemplate(resourceId.id)).then(hubResourceForTemplate);
                    }
                  }),
                ),
              } as HubResourceSection)
            : section,
        ),
      ),
    } as Hubs);
  };

  useEffect(() => {
    updateHubResources();
  }, [hubUpsertParams]);

  return (
    <>
      <TabNav tabs={Object.values(HubPreviewView)} onTabClick={onViewChange} />
      <div className="w-full overflow-auto">
        <div
          className={classNames(
            {
              'w-[1280px]': view === HubPreviewView.Desktop,
              'w-[640px]': view === HubPreviewView.Mobile,
            },
            'm-auto',
          )}
        >
          <HubDisplayPreview
            hub={hub}
            orgHub={orgHub as OrgHubs}
            onFavorite={onFavorite}
            onUnfavorite={onUnfavorite}
            previewView={view}
          />
        </div>
      </div>
    </>
  );
};

export default HubPreview;
